<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import DueDateIndicator from './DueDateIndicator.vue';

/**
 * Shared read-only content for regular clienteling task rows and Glovely AI
 * task suggestions.
 */
const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  contactName: {
    type: String,
    default: '',
  },
  projectTitle: {
    type: String,
    default: '',
  },
  typeLabel: {
    type: String,
    default: '',
  },
  completed: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const resolvedContactName = computed(
  () => props.contactName || t('TASKS.EMPTY_LINKS.CONTACT_UNAVAILABLE')
);

const resolvedProjectTitle = computed(() => {
  if (!props.task.linkedProjectId) return '';
  return props.projectTitle || t('TASKS.EMPTY_LINKS.PROJECT_UNAVAILABLE');
});

const resolvedTypeLabel = computed(() => {
  if (props.typeLabel) return props.typeLabel;
  return '';
});
</script>

<template>
  <span class="flex min-w-0 flex-1 flex-col gap-1">
    <span class="flex min-w-0 items-start">
      <span
        class="min-w-0 text-sm font-medium leading-5 text-n-slate-12"
        :class="{ 'line-through decoration-n-slate-9': completed }"
      >
        {{ task.title }}
      </span>
    </span>

    <span
      v-if="task.description && !compact"
      class="truncate text-xs leading-4 text-n-slate-11"
    >
      {{ task.description }}
    </span>

    <span
      class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-n-slate-10"
    >
      <span class="flex min-w-0 items-center gap-1">
        <span class="i-lucide-user size-3.5 shrink-0" />
        <span class="truncate">{{ resolvedContactName }}</span>
      </span>

      <DueDateIndicator :task="task" />
    </span>

    <span
      v-if="(resolvedProjectTitle && !compact) || resolvedTypeLabel"
      class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-n-slate-10"
    >
      <span
        v-if="resolvedProjectTitle && !compact"
        class="flex min-w-0 items-center gap-1"
      >
        <span class="i-lucide-shopping-bag size-3.5 shrink-0" />
        <span class="truncate">{{ resolvedProjectTitle }}</span>
      </span>

      <span v-if="resolvedTypeLabel" class="flex min-w-0 items-center gap-1">
        <span class="i-lucide-tag size-3.5 shrink-0" />
        <span class="truncate">{{ resolvedTypeLabel }}</span>
      </span>
    </span>
  </span>
</template>
